/**
 * productPageSchema.js — JSON-LD builders for the /card-machines/* cluster.
 *
 * SEOHead already emits Organization, LocalBusiness, BreadcrumbList (derived
 * from the path) and FAQPage (from the `faqs` prop). This helper supplies only
 * the nodes SEOHead doesn't know about: WebPage and — for real products we
 * supply — Product. The PayPal Reader comparison page passes
 * schemaType "Page" and gets WebPage only: we don't emit Product schema for
 * hardware we position as a comparison subject.
 *
 * Deliberately no `offers`, ratings or review nodes: pricing varies by
 * merchant agreement and unsupported values are a schema-QA failure.
 */

const PROD_URL = process.env.REACT_APP_PRODUCTION_URL || "https://candopayments.com";

export function buildProductPageSchema(product) {
  const path = `/card-machines/${product.slug}`;
  const url = `${PROD_URL}${path}`;

  const webPage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: product.metaTitle,
    description: product.metaDescription,
    inLanguage: "en-GB",
    isPartOf: { "@id": `${PROD_URL}/#website` },
    about: { "@id": `${PROD_URL}/#organization` },
    ...(product.heroImage && { primaryImageOfPage: product.heroImage }),
  };

  if (product.schemaType !== "Product") {
    return [webPage];
  }

  const productNode = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${url}#product`,
    name: product.name,
    description: product.quickAnswer.answer,
    url,
    brand: { "@type": "Brand", name: product.manufacturer },
    category: "Card Payment Terminal",
    image: [product.heroImage, ...(product.gallery || []).map((g) => g.src)].filter(Boolean),
    ...(product.shortName && { model: product.shortName }),
  };

  return [webPage, productNode];
}
